/**
 * Widget node (doc-widgets §D1) — the generic carrier for data-carrying blocks: charts, stat
 * tiles, tables. Same design as `chart.ts` (which stays registered forever for documents already
 * published with it): the whole payload rides one `data` attr so the markdown round-trip is a
 * single JSON blob, and `alt` keeps the widget visible to search and screen readers wherever a
 * host cannot render it.
 *
 * INTERNAL PM file; never re-exported from `index.ts`.
 */

import type { NodeSpec } from 'prosemirror-model';

export const WIDGET_NODE = 'widget';

export const widgetNode: NodeSpec = {
  group: 'block',
  atom: true,
  draggable: true,
  attrs: {
    data: { default: null },
    alt: { default: '' },
    partId: { default: null },
  },
  parseDOM: [
    {
      tag: 'div[data-widget]',
      getAttrs: (dom) => {
        const el = dom as HTMLElement;
        const raw = el.getAttribute('data-widget') ?? '';
        let data: unknown = null;
        try {
          data = raw ? JSON.parse(raw) : null;
        } catch {
          data = null;
        }
        return {
          data,
          alt: el.getAttribute('data-alt') ?? el.textContent ?? '',
          partId: el.getAttribute('data-part-id'),
        };
      },
    },
  ],
  toDOM: (node) => [
    'div',
    {
      // After a Yjs round-trip `data` may already BE a JSON string; stringifying it again
      // double-encodes. Pass strings through, stringify objects.
      'data-widget':
        typeof node.attrs.data === 'string'
          ? node.attrs.data
          : node.attrs.data
            ? JSON.stringify(node.attrs.data)
            : '',
      'data-alt': node.attrs.alt ?? '',
      ...(node.attrs.partId ? { 'data-part-id': node.attrs.partId } : {}),
      class: 'docit-widget-node',
    },
    node.attrs.alt ?? '',
  ],
};
